/**
 * CubesIllustration.tsx — kubeillustrasjon på blå bakgrunn (som «Deployment
 * options»-kortene på ibm.com). Brukes som bildeflate ved siden av
 * funksjonsakkordionen. Faste farger (egen blå bakgrunn, ikke tema).
 */

import { useId } from 'react';
import { Box } from '@/components/product/iso';
import styles from '@/components/product/illustrations.module.css';

const S = 38;
const OX = 336;
const OY = 120;
const project = (x: number, y: number, z = 0) =>
  `${(OX + (x - y) * 0.866 * S).toFixed(1)},${(OY + (x + y) * 0.5 * S - z * S).toFixed(1)}`;

type Tone = 'blue' | 'gray' | 'purple' | 'navy';

const CUBES: { x: number; y: number; tone: Tone }[] = [];
const PATTERN = [
  'gbgbgb',
  'bbpbgb',
  'gbnbpg',
  'bbbpbb',
  'gbpbbg',
  'bgbbgb',
];
PATTERN.forEach((row, y) =>
  [...row].forEach((c, x) =>
    CUBES.push({ x, y, tone: c === 'g' ? 'gray' : c === 'p' ? 'purple' : c === 'n' ? 'navy' : 'blue' }),
  ),
);

const TONES: Record<Tone, [string, string, string]> = {
  blue: ['#a6c8ff', '#4589ff', '#0f62fe'],
  gray: ['#f4f4f4', '#c6c6c6', '#a8a8a8'],
  purple: ['#e8daff', '#be95ff', '#8a3ffc'],
  navy: ['#4589ff', '#0043ce', '#001d6c'],
};

export default function CubesIllustration({ label }: { label: string }) {
  const uid = useId();
  const bg = `${uid}-bg`;
  // Bakerste rad først (maler bakfra og frem)
  const sorted = [...CUBES].sort((a, b) => a.x + a.y - (b.x + b.y));

  return (
    <svg className={styles.cubesFrame} viewBox="0 0 672 504" role="img" aria-label={label}>
      <defs>
        <linearGradient id={bg} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d0e2ff" />
          <stop offset="1" stopColor="#4589ff" />
        </linearGradient>
      </defs>
      <rect width="672" height="504" fill={`url(#${bg})`} />
      <g transform="translate(0 0)">
        {sorted.map(({ x, y, tone }) => (
          <Box
            key={`${x}-${y}`}
            x={x * 1.2}
            y={y * 1.2}
            w={0.9}
            d={0.9}
            h={tone === 'navy' ? 1.1 : 0.8}
            colors={TONES[tone]}
            project={project}
          />
        ))}
      </g>
    </svg>
  );
}
